import { useCallback, useEffect, useRef } from 'react';
import { fg } from '@confluence/feature-gating';
import { ONBOARDING_EXPERIENCES } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

const useLazyAmplitudeInit = () => {
	return useCallback(async () => {
		if (!fg('cc_engagement_amplitude_session_replay_gate')) {
			return; // no-op
		}
		const module = await import(
			/* webpackChunkName: "loadable-AmplitudeSessionReplay" */ './AmplitudeSessionReplay'
		);
		return module.AmplitudeSessionReplay.init();
	}, []);
};

const useLazyAmplitudeStop = () => {
	return useCallback(async () => {
		if (!fg('cc_engagement_amplitude_session_replay_gate')) {
			return; // no-op
		}
		const module = await import(
			/* webpackChunkName: "loadable-AmplitudeSessionReplay" */ './AmplitudeSessionReplay'
		);
		return module.AmplitudeSessionReplay.stop();
	}, []);
};

const isOnboardedWithin24Hours = (onboardingTimestamp: string | null | undefined): boolean => {
	if (!onboardingTimestamp) {
		return false;
	}
	const onboardingCompletedTime = parseInt(onboardingTimestamp, 10);
	if (isNaN(onboardingCompletedTime) || onboardingCompletedTime === 0) {
		return false;
	}
	return Date.now() - onboardingCompletedTime <= 24 * 60 * 60 * 1000;
};

export const useAmplitudeSessionReplay = ({
	skip = false,
	timeoutMs = 180000, // 3 minutes default
} = {}) => {
	const amplitudeInit = useLazyAmplitudeInit();
	const amplitudeStop = useLazyAmplitudeStop();
	const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
	const isInitializedRef = useRef(false);
	const analyticsEventFiredRef = useRef(false);
	const { data: onboardingStateData, loading: onboardingStateLoading } = useGetOnboardingState([
		ONBOARDING_EXPERIENCES.NTH_USER_ONBOARDING_COMPLETED_TIMESTAMP,
	]);
	const { nthUserOnboardingCompletedTimestamp } = deserializeState(onboardingStateData);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (skip || !fg('cc_engagement_amplitude_session_replay_gate')) {
			return;
		}

		// Initialize amplitude
		void amplitudeInit();
		isInitializedRef.current = true;

		// Set timeout to stop after specified time
		timeoutRef.current = setTimeout(() => {
			void amplitudeStop();
			isInitializedRef.current = false;
		}, timeoutMs);

		// Cleanup function
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
			if (isInitializedRef.current) {
				void amplitudeStop();
				isInitializedRef.current = false;
			}
		};
	}, [skip, amplitudeInit, amplitudeStop, timeoutMs]);

	useEffect(() => {
		if (
			skip ||
			!fg('cc_engagement_amplitude_session_replay_gate') ||
			!isInitializedRef.current ||
			analyticsEventFiredRef.current ||
			onboardingStateLoading
		) {
			return;
		}

		if (isOnboardedWithin24Hours(nthUserOnboardingCompletedTimestamp)) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'triggered',
					actionSubject: 'amplitudeSessionReplay',
					attributes: {
						isNewNthUser: true,
					},
				},
			}).fire();
			analyticsEventFiredRef.current = true;
		}
	}, [skip, nthUserOnboardingCompletedTimestamp, createAnalyticsEvent, onboardingStateLoading]);

	// Cleanup effect that always runs on unmount
	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
			if (isInitializedRef.current) {
				void amplitudeStop();
				isInitializedRef.current = false;
			}
		};
	}, [amplitudeStop]);

	// Handle pagehide to stop amplitude session replay when page closes
	useEffect(() => {
		if (skip || !fg('cc_engagement_amplitude_session_replay_gate')) {
			return;
		}

		const handlePageHide = () => {
			if (isInitializedRef.current) {
				void amplitudeStop();
				isInitializedRef.current = false;
			}
		};

		window.addEventListener('pagehide', handlePageHide);

		return () => {
			window.removeEventListener('pagehide', handlePageHide);
		};
	}, [skip, amplitudeStop]);
};
